import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useRoom } from "./RoomProvider";

/**
 * Onboarding — the AI's hiring event.
 *
 * First time a dispatcher opens the room. The room asks their name,
 * asks them to name their coworker. Then the coworker introduces itself.
 * 60 seconds. This will be the most-remembered moment of the product.
 */
export default function Onboarding() {
  const { identity, saveIdentity } = useRoom();
  const [step, setStep] = useState(identity ? -1 : 0);
  const [dispatcherName, setDispatcherName] = useState(identity?.dispatcher_name || "");
  const [coworkerName, setCoworkerName] = useState(identity?.coworker_name || "");

  if (step === -1) return null;

  return (
    <AnimatePresence>
      <motion.div className="onboard"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}>

        <motion.div className="onb-glow"
          animate={{ opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />

        {step === 0 && (
          <motion.div className="onb-scene"
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.9 }}>
            <div className="onb-kicker">welcome to the floor</div>
            <div className="onb-h1">before we begin</div>
            <div className="onb-line">what should i call you?</div>
            <input className="onb-input"
              value={dispatcherName}
              onChange={e => setDispatcherName(e.target.value)}
              placeholder="your name"
              autoFocus />
            <button className="onb-next"
              disabled={!dispatcherName.trim()}
              onClick={() => setStep(1)}>continue</button>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div className="onb-scene"
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7 }}>
            <div className="onb-kicker">hi, {dispatcherName.split(" ")[0].toLowerCase()}</div>
            <div className="onb-h1">{"i'll be working beside you"}</div>
            <div className="onb-line">
              {"i already know the network. i'll watch the trips, the drivers, credentials, weather, brokers, and everything else in the room — so you don't have to. i'll speak up when something needs you."}
            </div>
            <div className="onb-line" style={{ marginTop: 18 }}>
              what would you like to call me?
            </div>
            <input className="onb-input"
              value={coworkerName}
              onChange={e => setCoworkerName(e.target.value)}
              placeholder="a name — anything"
              autoFocus />
            <div className="onb-hint">
              {"you can rename me any time. i'll remember."}
            </div>
            <div className="onb-actions">
              <button className="onb-ghost" onClick={() => setStep(0)}>back</button>
              <button className="onb-next"
                disabled={!coworkerName.trim()}
                onClick={() => setStep(2)}>continue</button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div className="onb-scene"
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9 }}>
            <div className="onb-kicker">{coworkerName.toLowerCase()} · introducing myself</div>
            <div className="onb-h1">nice to meet you, {dispatcherName.split(" ")[0]}.</div>
            <div className="onb-line">
              {"i'll learn your patterns. when you eat, who you trust, how you handle tough calls, which providers you lean on. i'll get better at helping you. and i'll be honest when i don't know something."}
            </div>
            <div className="onb-line" style={{ marginTop: 18, opacity: 0.7 }}>
              the room is already alive. take a look. press <kbd>/</kbd> when you
              want to speak to me directly, or click anything you see.
            </div>
            <div className="onb-actions">
              <button className="onb-next"
                onClick={() => {
                  saveIdentity({
                    dispatcher_name: dispatcherName.trim(),
                    coworker_name: coworkerName.trim(),
                    joined_at: new Date().toISOString(),
                  });
                  setStep(-1);
                }}>
                open the floor
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
